import { ELEMENT_ATTRIBUTE_GROUP, ELEMENT_GROUPS } from './../utils';

const StripeElementFields = {
  group: ELEMENT_GROUPS.OTHER,
  label: 'Stripe',
  icon: 'DocumentIcon',
  description: 'Adds a Stripe payment experience',
  properties: [
    {
      label: 'Description',
      name: 'description',
      editor: 'textbox',
      default: 'My Stripe Checkout',
      description:
        'A friendly name for this Stripe Element. This description is not used in payment processing',
      options: [],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Functional Mode',
      name: 'functionalMode',
      description:
        'How is this Stripe Element operating? Choose `Sale` if it is being used to show a payment sheet to sell a product or subscription. Choose `Charge an Ad-Hoc Amount` to sell something at a variable price. Choose listener if this Stripe Element is just listening for changes to payments and subscriptions in the database. When it detects changes it will update the users internal claims in the app. This is required if we want to inform the app instantly when there are any changes instead of waiting for the user to logout and login or an id token refresh. Choose Portal if this is being used to allow the user to manage their payment details in an internal web browser.',
      editor: 'dropdown',
      default: 'sale',
      options: [
        { label: 'Sell a Subscription or Product', value: 'sale' },
        { label: 'Charge an Ad-Hoc Amount', value: 'adhoc' },
        { label: 'Manage Payment Details Portal', value: 'portal' },
        { label: 'Listener', value: 'listener' },
        { label: 'Verify Identity', value: 'identity' },
        { label: 'Connect Onboarding', value: 'connect_onboarding' },
        { label: 'Connect Dashboard', value: 'connect_dashboard' }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Payment Type',
      name: 'paymentType',
      description:
        'Specify whether the payment should be taken immediately or is an intent to take payment',
      editor: 'dropdown',
      default: 'payment',
      options: [
        { label: 'Take Payment Immediately', value: 'payment' },
        { label: 'Setup The Payment Method', value: 'setup' },
        { label: 'Pre-Authorise Amount', value: 'preauth' }
      ],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Collection Name',
      name: 'collectionPath',
      editor: 'textbox',
      default: 'stripe_customers',
      description:
        'The name of the firestore collection that holds all purchase information for a customer',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'listener'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Merchant Category Code',
      name: 'merchantCategoryCode',
      editor: 'textbox',
      default: '',
      description:
        'A code (which can accept placeholders) as from https://stripe.com/docs/connect/setting-mcc#list If supplied, will auto-populate the stripe onboarding data in the stripe onboarding flow.',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'connect_onboarding'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Connect Product Description',
      name: 'connectProductDescription',
      editor: 'textbox',
      default: '',
      description:
        'A description of the product or service that the connect onboarding user is supplying to the marketplace. This will auto-fill the stripe onboarding flow.',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'connect_onboarding'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Is Subscription',
      name: 'isSubscription',
      description:
        'Set to true, if the user will be signing up to a subscription',
      editor: 'switch',
      default: true,
      options: [],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Product ID (Test Mode)',
      name: 'productIdTest',
      editor: 'textbox',
      description:
        'Enter a product id from Stripe that the price id belongs to in Test Mode',
      default: '',
      options: [],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        },
        {
          field: 'isSubscription',
          operator: 'DOES_NOT_EQUAL',
          value: true
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Product ID (Live)',
      name: 'productIdLive',
      editor: 'textbox',
      description:
        'Enter a product id from Stripe that the price id belongs to in Live Mode',
      default: '',
      options: [],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        },
        {
          field: 'isSubscription',
          operator: 'DOES_NOT_EQUAL',
          value: true
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Price ID or Amount Source',
      name: 'priceIdSource',
      description:
        'If functional mode is to sell a subscription or product, every Stripe purchase must have a corresponding Price ID in Stripe, regardless of whether it is a one time payment or a payment for a Subscription. You can choose to enter a particular price id or retrieve the price id from the current record. If you are charging an ad-hoc amount you can get it from a static amount entered or a data source selected record. (Not required when in Listener Mode)',
      editor: 'dropdown',
      default: true,

      options: [
        { label: 'Static Value', value: '' },
        { label: 'Data Source Selected Record', value: 'record' }
      ],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },

    {
      label: 'Price ID (Test Mode)',
      name: 'priceIdTest',
      editor: 'textbox',
      description:
        'Enter a price id from Stripe for Test Mode (Not required when in Listener Mode)',
      default: '',
      options: [],
      displayWhen: [
        {
          field: 'priceIdSource',
          operator: 'DOES_NOT_EQUAL',
          value: 'record'
        },
        {
          field: 'functionalMode',
          operator: 'DOES_NOT_EQUAL',
          value: 'adhoc'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Amount',
      name: 'amount',
      editor: 'textbox',
      description: 'Enter the ad-hoc amount to charge',
      default: '',
      options: [],
      displayWhen: [
        {
          field: 'priceIdSource',
          operator: 'DOES_NOT_EQUAL',
          value: 'record'
        },
        {
          field: 'functionalMode',
          operator: '=',
          value: 'adhoc'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Price ID (Live)',
      name: 'priceIdLive',
      editor: 'textbox',
      description:
        'Enter a price id from Stripe for Live Mode. (Not required when in Listener Mode)',
      default: '',
      options: [],
      displayWhen: [
        {
          field: 'priceIdSource',
          operator: 'DOES_NOT_EQUAL',
          value: 'record'
        },
        {
          field: 'functionalMode',
          operator: 'DOES_NOT_EQUAL',
          value: 'adhoc'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Checkout Session ID Field',
      name: 'checkoutSessionIdField',
      description:
        'Choose a field from the data source that will be used to store the id of the checkout session. If the payment type is setup, this ID will be used to charge the card later.',
      editor: 'dropdown',
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      default: '',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Connect User ID Field',
      name: 'connectUserIdField',
      description:
        'Choose a field from the data source which contains the userId of a user record in the users collection that has a value in its stripeConnectAccountId field. If selected and the user denoted by this field value has a Stripe Connect Account ID, they will be setup as the recipient of the payment.',
      editor: 'dropdown',
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      default: '',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Connect Platform Fee Field',
      name: 'connectPlatformFeeField',
      description:
        'Choose a field from the data source which contains the platform fee amount. This will be used if a Stripe Connect User is associated with the payment.',
      editor: 'dropdown',
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      default: '',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Data Binding Source',
      name: 'datasourceId',
      description:
        'Choose a datasource where this element will get its data from in order to obtain either the Price ID or the Amount to charge or record the verification status of an identity check.',
      editor: 'dropdown',
      function: {
        name: 'getDynamicDataSources',
        params: ['app.id']
      },
      default: '',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc', 'identity']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Data Binding Field (PriceId or Amount)',
      name: 'sourceField',
      editor: 'dropdown',
      description:
        'The field from the underlying datasource will be used as the value of this element. The field in the data source that contains the Stripe Price ID or Amount. (Not required when in Listener Mode)',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        },
        {
          field: 'priceIdSource',
          operator: '=',
          value: 'record'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Data Binding Field (Description)',
      name: 'descriptionField',
      editor: 'dropdown',
      description:
        'The field from the underlying datasource will be used the description of the purchase',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'adhoc'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Identity Verification Status Field',
      name: 'identityVerificationStatusField',
      editor: 'dropdown',
      description:
        'The field from the underlying datasource will be used to record or read the verified status from.',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'identity'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Identity Verification Status Description Field',
      name: 'identityVerificationStatusDescriptionField',
      editor: 'dropdown',
      description:
        'The field from the underlying datasource will be used to record or read a description of verified status.',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'identity'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Description Markup',
      name: 'descriptionMarkup',
      editor: 'textbox',
      description:
        'Markup that can be used to create a more complex description i.e. {reference} {description}',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'adhoc'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Data Binding Field (Reference)',
      name: 'referenceField',
      editor: 'dropdown',
      description:
        'The field from the underlying datasource will be used as the reference number for the purchase',
      default: '',
      options: [],
      function: {
        name: 'getDynamicDataSourceFields',
        values: [],
        params: ['app.id', 'element.datasourceId']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'adhoc'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Button Text',
      name: 'buttonText',
      editor: 'textbox',
      description:
        'The text to display on the button. i.e. Setup Payouts, Setup Payment Method etc',
      default: 'Setup Payment Method',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc', 'connect_onboarding', 'connect_dashboard']
        }
      ],
      options: [],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'Use Non-Theme Button',
      name: 'useNonThemeButton',
      editor: 'boolean',
      description: 'If true will customise the button different to the theme',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Button Mode',
      name: 'mode',
      editor: 'dropdown',
      default: 'text',
      description: 'The mode of button',
      options: [
        {
          label: 'Text',
          value: 'text'
        },
        {
          label: 'Outlined',
          value: 'outlined'
        },
        {
          label: 'Contained',
          value: 'contained'
        }
      ],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Show Success Message',
      name: 'showSuccessMessage',
      description:
        'Set to true, will show the user a custom success message in a pop',
      editor: 'switch',
      default: true,
      options: [],
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      name: 'successMessage',
      label: 'Success Message',
      description: '',
      editor: 'textbox',
      type: 'string',
      default: '',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        },
        { field: 'showSuccessMessage', operator: '=', value: true }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.CONTENT
    },
    {
      label: 'On Payment Details Entered Action',
      name: 'action',
      editor: 'dropdown',
      default: '',
      description:
        'An action to run when the payment details have been entered',
      options: [],
      function: {
        name: 'getAppActions',
        params: ['app.id']
      },
      displayWhen: [
        {
          field: 'functionalMode',
          operator: 'IN',
          value: ['sale', 'adhoc']
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.BEHAVIOUR
    },
    {
      label: 'Width',
      name: 'width',
      editor: 'number',
      description: 'The width of the button',
      default: 245,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    'font',
    'alignment',
    {
      name: 'buttonTextAlignment',
      label: 'Button Text Alignment',
      description: '',
      editor: 'alignment',
      type: 'string',
      default: 'justify-center',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    'margin',
    {
      name: 'iconMargin',
      label: 'Icon Margin',
      description: '',
      editor: 'boundary',
      type: 'string',
      default: '0 10 0 0',
      displayWhen: [
        {
          field: 'functionalMode',
          operator: '=',
          value: 'sale'
        },
        { field: 'showIcon', operator: '=', value: true }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      name: 'padding',
      label: 'Padding',
      description: '',
      editor: 'boundary',
      type: 'string',
      default: '10 10 10 10',
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      label: 'Border Color',
      name: 'borderColor',
      editor: 'color',
      description: 'The border color of a list item',
      default: '#182fa4',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Border Radius',
      name: 'borderRadius',
      editor: 'number',
      description: 'The border radius of the element',
      default: 0,
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    },
    {
      name: 'color',
      label: 'Color',
      description: '',
      editor: 'color',
      type: 'string',
      default: '#ffffff',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      name: 'backgroundColor',
      label: 'Background Color',
      description: '',
      editor: 'color',
      type: 'string',
      default: '#182fa4',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      name: 'disabledColor',
      label: 'Disabled Color',
      description: '',
      editor: 'color',
      type: 'string',
      default: '#ffffff',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      name: 'disabledBackgroundColor',
      label: 'Disabled Background Color',
      description: '',
      editor: 'color',
      type: 'string',
      default: '#182fa4',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Payment Processing Spinner Color',
      name: 'processingSpinnerColor',
      editor: 'color',
      description:
        'The color of the activity indicator spinner whilst waiting for a status update from the server.',
      default: '#182fa4',
      group: ELEMENT_ATTRIBUTE_GROUP.COLORS
    },
    {
      label: 'Payment Processing Spinner Size',
      name: 'processingSpinnerSize',
      editor: 'dropdown',
      description:
        'The size of the activity indicator spinner whilst waiting for a status update from the server.',
      default: 'small',
      options: [
        { label: 'Small', value: 'small' },
        { label: 'Large', value: 'large' }
      ],
      group: ELEMENT_ATTRIBUTE_GROUP.LAYOUT
    }
  ]
};

export default StripeElementFields;
